// API Configuration (must be set via NEXT_PUBLIC_API_BASE_URL in .env or build args)
// CI: no-op touch to trigger frontend build pipeline (2026-09-28)
import { logMissingFrontendEnv } from "./envCheck";

export const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

/** Standalone MeritLens analytics ingestion service (not the main API, not GA). */
export const ANALYTICS_API_URL =
  process.env.NEXT_PUBLIC_ANALYTICS_API_URL ?? "";

/** First-party analytics client. Unset or any value other than "true" keeps it off. */
export const ANALYTICS_ENABLED =
  process.env.NEXT_PUBLIC_ANALYTICS_ENABLED?.trim().toLowerCase() === "true";

/** Candidate-facing page after interview submission (no scores on this route). */
export const INTERVIEW_THANK_YOU_PATH = "/interview/thank-you";

/** Candidate-facing analysis page after interview (consumer flow); enterprise pipeline uses thank-you instead. */
export const INTERVIEW_RESULTS_PATH = "/results";

// Courses API Configuration
export const COURSES_API_BASE_URL =
  process.env.NEXT_PUBLIC_COURSES_API_BASE_URL;

export const LAB_EXERCISES_URL =
  process.env.NEXT_PUBLIC_LAB_EXERCISES_URL || "https://academy.techverito.com/lab-exercises";

/** Full Courses Platform track URL (base from env + path e.g. /tracks/...). */
export function getCoursesPlatformTrackUrl(trackPath: string): string | null {
  const base = (COURSES_API_BASE_URL || "").trim().replace(/\/$/, "");
  if (!base || !trackPath) return null;
  const path = trackPath.startsWith("/") ? trackPath : `/${trackPath}`;
  return `${base}${path}`;
}

export function getEditorWebSocketUrl(
  projectEnrollmentId: string,
  token?: string,
): string {
  const base = COURSES_API_BASE_URL || "";
  if (!base.trim()) {
    return "";
  }
  const wsBase = base
    .replace(/^https:\/\//, "wss://")
    .replace(/^http:\/\//, "ws://");
  let url = `${wsBase}/api/editor/ws?channel_id=${encodeURIComponent(projectEnrollmentId)}`;
  if (token) {
    url += `&token=${encodeURIComponent(token)}`;
  }
  return url;
}

// Webinar API Configuration
export const WEBINAR_BACKEND_URL = process.env.NEXT_PUBLIC_WEBINAR_BACKEND_URL;
export const WEBINAR_WS_URL = process.env.NEXT_PUBLIC_WEBINAR_WS_URL;

// Google OAuth Configuration
export const GOOGLE_CLIENT_ID =
  process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ||
  process.env.GOOGLE_CLIENT_ID ||
  "";

/**
 * Microsoft Entra (Outlook / work accounts) — enterprise SSO popup; must match API MICROSOFT_SSO_CLIENT_ID.
 * Stage/prod images bake these from GitLab CI variables in stage-build.sh, not from a committed .env file.
 */
export const MICROSOFT_CLIENT_ID =
  process.env.NEXT_PUBLIC_MICROSOFT_CLIENT_ID || "";

const _msAuthorityOverride =
  process.env.NEXT_PUBLIC_MICROSOFT_AUTHORITY?.trim();
const _msTenantId = process.env.NEXT_PUBLIC_MICROSOFT_TENANT_ID?.trim();

/**
 * MSAL authority (must match how the app is registered in Entra ID).
 *
 * - **Single-tenant** app → set `NEXT_PUBLIC_MICROSOFT_TENANT_ID` (Directory tenant ID GUID), or set
 *   full `NEXT_PUBLIC_MICROSOFT_AUTHORITY` to `https://login.microsoftonline.com/{tenant-id}`.
 *   Without a tenant, sign-in can fail with **AADSTS90019**.
 * - **Multitenant work accounts** → omit both; defaults to `/organizations` (no personal accounts).
 * - **Work + personal Microsoft accounts** → `NEXT_PUBLIC_MICROSOFT_AUTHORITY=https://login.microsoftonline.com/common`
 *   and enable personal accounts on the app registration.
 */
export const MICROSOFT_AUTHORITY =
  _msAuthorityOverride ||
  (_msTenantId
    ? `https://login.microsoftonline.com/${_msTenantId}`
    : "https://login.microsoftonline.com/organizations");

/**
 * Optional: verified Azure AD domain (e.g. `contoso.com`). Sent as `domain_hint` so users with the same
 * email on both Microsoft personal (live.com) and work accounts are steered to the organizational IdP.
 * Helps avoid **AADSTS50020** when the app is single-tenant work-only.
 */
export const MICROSOFT_LOGIN_DOMAIN_HINT =
  process.env.NEXT_PUBLIC_MICROSOFT_DOMAIN_HINT?.trim() || "";

/**
 * MSAL redirect URI override. Must match an Entra SPA redirect URI exactly.
 * If empty, the client uses `${origin}/enterprise/sign-in` at runtime.
 */
export const MICROSOFT_REDIRECT_URI_OVERRIDE =
  process.env.NEXT_PUBLIC_MICROSOFT_REDIRECT_URI?.trim() || "";

// GitHub OAuth Configuration
export const GITHUB_CLIENT_ID =
  process.env.NEXT_PUBLIC_GITHUB_CLIENT_ID ||
  process.env.GITHUB_CLIENT_ID ||
  "";

// Google Analytics Configuration
// GA4 Measurement ID (format: G-XXXXXXXXXX)
export const GA_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || "";

// Google Ads Configuration
// Conversion / remarketing ID (format: AW-XXXXXXXXXX)
export const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID || "";

// Google Tag Manager Configuration
// GTM Container ID (format: GTM-XXXXXXX)
export const GTM_CONTAINER_ID = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID || "";

// Meta (Facebook) Pixel Configuration
// Pixel ID from Meta Events Manager (numeric string)
export const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID || "";

// Debug / App Environment Configuration
// Explicit app environment to distinguish local dev from staging/production,
// even when staging runs `npm run dev` (NODE_ENV === 'development').
// Expected values: 'development' | 'staging' | 'production'
export const APP_ENV = process.env.NEXT_PUBLIC_APP_ENV || "development";

/**
 * JD video silence auto-submit diagnostics (`console.log` Timer A/B + countdown in JDVideoInterviewSession).
 * Only true when `NEXT_PUBLIC_APP_ENV` is development, staging, or stage — never in production.
 */
export const ENABLE_JD_SILENCE_CONSOLE_LOGS = (() => {
  const e = APP_ENV.trim().toLowerCase();
  return (
    e === "development" || e === "staging" || e === "stage"
  );
})();

// Enable debug logs in UI (defaults to false if not set, set to 'true' to enable).
// Never enabled in production, even if NEXT_PUBLIC_DEBUG is set.
export const DEBUG =
  APP_ENV !== "production" && process.env.NEXT_PUBLIC_DEBUG === "true";

// Support Bot Feature Flag
export const SUPPORT_FEATURE_ENABLED =
  process.env.NEXT_PUBLIC_SUPPORT_FEATURE_ENABLED === 'true';

// Phase 2: enables the free-text chat composer for logged-in users.
// Set to 'true' only when OPENAI_API_KEY + SUPPORT_LLM_DISABLED=false are configured on the backend.
export const SUPPORT_AI_ENABLED =
  process.env.NEXT_PUBLIC_SUPPORT_AI_ENABLED === 'true';

if (typeof window !== 'undefined') {
  console.debug(
    '[support] feature_enabled=%s ai_enabled=%s raw_feature=%s raw_ai=%s',
    SUPPORT_FEATURE_ENABLED,
    SUPPORT_AI_ENABLED,
    process.env.NEXT_PUBLIC_SUPPORT_FEATURE_ENABLED,
    process.env.NEXT_PUBLIC_SUPPORT_AI_ENABLED,
  );
}

// Code/SQL Questions Streaming Feature Flag
// When enabled, routes code_completion and database_sql / database_query questions to streaming endpoint
// When disabled (default), code/SQL questions continue using /process-interview endpoint
export const USE_STREAMING_FOR_CODE_QUESTIONS =
  process.env.NEXT_PUBLIC_USE_STREAMING_FOR_CODE_QUESTIONS === "true";

// When true (default), missing SSE prefetch falls back to POST /interview/next-question.
// Set NEXT_PUBLIC_NEXT_QUESTION_POST_FALLBACK=false to surface an error instead (stream-first QA).
export const NEXT_QUESTION_POST_FALLBACK =
  process.env.NEXT_PUBLIC_NEXT_QUESTION_POST_FALLBACK !== "false";

/** When true (with feedbackAsyncPipelineClient on JDVideoInterviewSession only): SSE defers feedback to resolve endpoint. Default false. */
export const ENABLE_INTERVIEW_FEEDBACK_ASYNC_PIPELINE_CLIENT =
  process.env.NEXT_PUBLIC_ENABLE_INTERVIEW_FEEDBACK_ASYNC_PIPELINE === "true";

/**
 * When true, interview live captions use Deepgram via backend WS proxy
 * (`/ws/transcription/...`) instead of the browser Web Speech API.
 * Default false — keep Web Speech until Deepgram is validated in each env.
 */
export const USE_DEEPGRAM_STT =
  process.env.NEXT_PUBLIC_USE_DEEPGRAM_STT === "true";

/**
 * When true, the microphone stays open during a code or SQL question purely so the
 * server can detect a second voice, the same way it already does for spoken answers.
 * Nothing said is transcribed into the answer or stored; only the detection is kept.
 *
 * Default false. This turns the mic on in a window where it used to be off, so it is
 * opt-in per environment, and the candidate is shown a notice whenever it is listening.
 */
export const CODING_VOICE_MONITOR_ENABLED =
  process.env.NEXT_PUBLIC_CODING_VOICE_MONITOR_ENABLED === "true";

// JD Video Interview Feature Flag (build-time)
// When enabled, JD-based interviews redirect to /jd-video-interview instead of /interview-intro
// Driven by TF_VIDEO_INTERVIEW_ENABLED — no separate env var needed.
export const ENABLE_JD_VIDEO_INTERVIEW =
  process.env.NEXT_PUBLIC_TF_VIDEO_INTERVIEW_ENABLED?.trim().toLowerCase() === "true";

// Mock Interview UI Layout Flag (build-time)
// Controls which UI layout is used for mock (skill assessment) interviews.
// Driven by TF_VIDEO_INTERVIEW_ENABLED — no separate env var needed.
export const MOCK_INTERVIEW_VIDEO_UI =
  process.env.NEXT_PUBLIC_TF_VIDEO_INTERVIEW_ENABLED?.trim().toLowerCase() === "true";

// JD Video Interview Test Mode (build-time)
// Only enabled when APP_ENV === 'development' to prevent accidental activation
// on staging/production, even if NEXT_PUBLIC_ENABLE_JD_VIDEO_TEST_MODE is true there.
// When enabled alongside ENABLE_JD_VIDEO_INTERVIEW, shows toggle to use predefined question set
// (Java, PostgreSQL, Software Developer, Junior) - skips JD extraction for testability
export const ENABLE_JD_VIDEO_TEST_MODE =
  APP_ENV === "development" &&
  process.env.NEXT_PUBLIC_ENABLE_JD_VIDEO_TEST_MODE === "true";

// Show interview countdown timer in the JD video interview UI.
// Default false — set NEXT_PUBLIC_SHOW_INTERVIEW_TIMER=true to enable on any environment.
export const SHOW_INTERVIEW_TIMER =
  process.env.NEXT_PUBLIC_SHOW_INTERVIEW_TIMER === "true";

// JD template demo: deterministic question list from templateDemoQuestions.ts (client + API flags).
export const ENABLE_TEMPLATE_DEMO_UI =
  process.env.NEXT_PUBLIC_ENABLE_TEMPLATE_DEMO_MODE === "true";

// Enabled when NEXT_PUBLIC_ENABLE_RESUME_TEST_MODE=true AND APP_ENV is not 'production'.
// This way any non-prod env (development, stage, qa, etc.) works without code changes.
export const ENABLE_RESUME_TEST_MODE =
  APP_ENV !== "production" &&
  process.env.NEXT_PUBLIC_ENABLE_RESUME_TEST_MODE === "true";

// Job Monitor admin panel visibility (build-time).
// Set NEXT_PUBLIC_ENABLE_JOB_MONITOR=true to show the Job Monitor section in the admin sidebar.
// Default: false (hidden in all envs unless explicitly enabled).
export const ENABLE_JOB_MONITOR =
  process.env.NEXT_PUBLIC_ENABLE_JOB_MONITOR === "true";

// Generate Questions admin tab (build-time).
// Default: on. Set NEXT_PUBLIC_ENABLE_GENERATE_QUESTIONS=false to hide the tab.
export const ENABLE_GENERATE_QUESTIONS =
  process.env.NEXT_PUBLIC_ENABLE_GENERATE_QUESTIONS !== "false";

// System Design Interview Feature Flag (build-time)
// Production: off unless NEXT_PUBLIC_ENABLE_SYSTEM_DESIGN_INTERVIEW is 'true' or '1' (trimmed, case-insensitive).
// Staging / development: on unless set to 'false' (same as before).
// IMPORTANT: NEXT_PUBLIC_* is inlined at build time — set in Docker/build env and rebuild to change.
const _systemDesignFlagRaw =
  process.env.NEXT_PUBLIC_ENABLE_SYSTEM_DESIGN_INTERVIEW;
const _systemDesignExplicitOnStr =
  _systemDesignFlagRaw == null
    ? ""
    : String(_systemDesignFlagRaw).trim().toLowerCase();
const _systemDesignExplicitOn =
  _systemDesignExplicitOnStr === "true" || _systemDesignExplicitOnStr === "1";
export const ENABLE_SYSTEM_DESIGN_INTERVIEW =
  APP_ENV === "production"
    ? _systemDesignExplicitOn
    : _systemDesignFlagRaw !== "false";

// Payments / Referral / Transactions UI (build-time)
// When false, hide Referral program, My Transactions, and payment amounts.
// IMPORTANT: NEXT_PUBLIC_* is inlined at build time — set the env in your stage/build environment and run a new build + deploy for changes to apply.
// Enabled only when explicitly 'true' or '1' (case-insensitive). Unset, empty, 'false', '0' = disabled.
const _paymentsEnabledRaw = process.env.NEXT_PUBLIC_PAYMENTS_ENABLED;
const _paymentsEnabledStr =
  _paymentsEnabledRaw == null
    ? ""
    : String(_paymentsEnabledRaw).trim().toLowerCase();
export const PAYMENTS_ENABLED =
  _paymentsEnabledStr === "true" || _paymentsEnabledStr === "1";

// Combo Plan feature flag (build-time, default false).
// true = show multi-credit packs on pricing section; backend also filters.
// NEVER disables consumption of already-purchased combo credits.
export const TF_ENABLE_COMBO_PLAN =
  process.env.NEXT_PUBLIC_TF_ENABLE_COMBO_PLAN === "true";

export const TF_ENABLE_ENTERPRISE_DEMO_FLOW =
  process.env.NEXT_PUBLIC_TF_ENABLE_ENTERPRISE_DEMO_FLOW === "true";

// Course Access Control (Whitelisting)
export const COURSE_WHITELIST_ENABLED =
  process.env.NEXT_PUBLIC_COURSE_WHITELIST_ENABLED !== "false";
export const COURSE_EMAIL_WHITELIST =
  process.env.NEXT_PUBLIC_COURSE_EMAIL_WHITELIST ||
  process.env.COURSE_EMAIL_WHITELIST ||
  "";
export const COURSE_DOMAIN_WHITELIST =
  process.env.NEXT_PUBLIC_COURSE_DOMAIN_WHITELIST || "";

// TTS playback speed for question audio only (1.0 = normal). Used only when playing bot question/clarification TTS.
// Clamped to 0.5–2.0; invalid or missing env falls back to 1.0 so existing behavior is unchanged.
const ttsQuestionRateRaw = process.env.NEXT_PUBLIC_TTS_QUESTION_PLAYBACK_RATE;
const ttsQuestionRateParsed = ttsQuestionRateRaw
  ? Number(ttsQuestionRateRaw)
  : 1;
export const TTS_QUESTION_PLAYBACK_RATE =
  Number.isFinite(ttsQuestionRateParsed) && ttsQuestionRateParsed > 0
    ? Math.min(Math.max(ttsQuestionRateParsed, 0.5), 2)
    : 1;

// API Endpoints
export const API_ENDPOINTS = {
  LOGIN: "/users/login",
  LOGIN_WITH_OTP: "/users/login-with-otp",
  REGISTER: "/users/register",
  GOOGLE_LOGIN: "/users/google-login",
  GITHUB_LOGIN: "/users/github-login",
  CURRENT_USER: "/users/current-user",
  UPDATE_NAME: "/users/update-name",
  CHECK_ADMIN: "/users/me/is-admin",
  INTERVIEW_OPTIONS: "/user/interview_options",
  /** GET — true if recruiter added candidate with resume_url (no Resume row required). */
  HAS_RECRUITER_PROVIDED_RESUME: "/user/has-recruiter-provided-resume",
  ENSURE_INTERVIEW: "/user/ensure-interview",
  START_INTERVIEW_UNIFIED: "/interview/start",
  VALIDATE_START_INTERVIEW: "/interview/validate-start",
  /** GET — authoritative interview_type and status from UserInterview. */
  INTERVIEW_META: (interviewId: string | number) =>
    `/interview/${interviewId}/meta`,
  /** GET — caller's latest In Progress / Temp Abandoned interview (session recovery). */
  ACTIVE_INTERVIEW: "/user/active-interview",
  /** GET — caller's current non-completed JD-based interview (JD email links without jd_id). */
  CURRENT_JD_INTERVIEW: "/user/jd-interviews/current",
  /** POST — always creates a new UserInterview for system design practice (then call START_INTERVIEW_UNIFIED). */
  SYSTEM_DESIGN_CREATE_INTERVIEW: "/interview/system-design/create",
  /** PATCH to log device selection for interview session (audio_input_label, audio_output_label). */
  INTERVIEW_DEVICE: (interviewId: string | number) =>
    `/interview/${interviewId}/device`,
  /** POST with { transcript, is_silence }; returns { ack: IntroMessageData | null }. JD only. */
  CANDIDATE_INTRO_ACK: (interviewId: string | number) =>
    `/interview/${interviewId}/candidate-intro-ack`,
  USER_ACCESS: (productType: string) => `/user/access/${productType}`,
  JD_INTERVIEW_ACCESS: "/user/jd-interview-access", // DEPRECATED: Use USER_ACCESS('jd_prep')
  SKILL_ASSESSMENT_ACCESS: "/user/skill-assessment-access", // DEPRECATED: Use USER_ACCESS('skill_assessment')
  PRODUCT_BY_ID: (id: string) => `/user/products/${id}`,
  /** GET (public) — active pricing packs with credit breakdown. */
  PRODUCT_PACKS: "/products/packs",
  PAYMENT_START: "/payments/start",
  PAYMENT_CHECKOUT_PREVIEW: "/payments/checkout/preview",
  PAYMENT_CHECKOUT_CONTACT: "/payments/checkout/contact",
  PAYMENT_INITIATE: "/payments/initiate",
  PAYMENT_VERIFY: "/payments/verify",
  PAYMENT_CREDITS: "/payments/credits",
  PAYMENT_CONFIG: "/payments/config",
  ENTERPRISE_PACKAGE_CONFIG: "/payments/enterprise/package-config",
  ENTERPRISE_VALIDATE_BILLING: "/payments/enterprise/validate-billing",
  /** GET — streams the server-generated tax invoice PDF (application/pdf). Requires auth. */
  PAYMENT_INVOICE_PDF: (txnid: string) =>
    `/payments/${encodeURIComponent(txnid)}/invoice/pdf`,
  ORG_PAYMENT_HISTORY: "/payments/org/my-history",
  /** GET — streams the server-generated tax invoice PDF for an org payment. Requires auth. */
  ORG_PAYMENT_INVOICE_PDF: (paymentId: string) =>
    `/payments/org/${encodeURIComponent(paymentId)}/invoice/pdf`,
  /** GET — download all org invoices as a ZIP file for a given date range. Requires auth (owner only). */
  ORG_INVOICES_DOWNLOAD: (fromDate: string, toDate: string) =>
    `/payments/org/invoices/download?from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`,
  /** GET — finance: check if current user has finance access. */
  FINANCE_ME: "/finance/me",
  /** GET — marketing: check if current user has marketing or admin access. */
  MARKETING_ME: "/marketing/me",
  MARKETING_SUMMARY: (fromDate: string, toDate: string, campaign?: string) => {
    const params = new URLSearchParams({
      from_date: fromDate,
      to_date: toDate,
    })
    if (campaign) params.set("campaign", campaign)
    return `/marketing/analytics/summary?${params.toString()}`
  },
  /** GET - marketing: funnel and step metadata, no aggregation. */
  MARKETING_FUNNEL_CATALOG: "/marketing/analytics/funnels",
  /** GET - marketing: identified people active in the range, for the Leads page. */
  MARKETING_LEADS: (
    fromDate: string,
    toDate: string,
    options?: {
      campaign?: string
      funnel?: string
      step?: string
      query?: string
      eventName?: string
      stage?: string
      limit?: number
      offset?: number
    },
  ) => {
    const params = new URLSearchParams({
      from_date: fromDate,
      to_date: toDate,
      limit: String(options?.limit ?? 25),
      offset: String(options?.offset ?? 0),
    })
    if (options?.campaign) params.set("campaign", options.campaign)
    if (options?.funnel) params.set("funnel", options.funnel)
    if (options?.step) params.set("step", options.step)
    if (options?.query) params.set("q", options.query)
    if (options?.eventName) params.set("event_name", options.eventName)
    if (options?.stage) params.set("stage", options.stage)
    return `/marketing/analytics/leads?${params.toString()}`
  },
  MARKETING_LEAD_JOURNEY: (
    userId: string,
    fromDate: string,
    toDate: string,
    limit = 200,
  ) => {
    const params = new URLSearchParams({
      from_date: fromDate,
      to_date: toDate,
      limit: String(limit),
    })
    return `/marketing/analytics/leads/${encodeURIComponent(userId)}/journey?${params.toString()}`
  },
  MARKETING_CAMPAIGNS: (fromDate: string, toDate: string) =>
    `/marketing/analytics/campaigns?from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`,
  MARKETING_FUNNEL: (
    funnelId: string,
    fromDate: string,
    toDate: string,
    campaign?: string,
  ) => {
    const params = new URLSearchParams({
      from_date: fromDate,
      to_date: toDate,
    })
    if (campaign) params.set("campaign", campaign)
    return `/marketing/analytics/funnels/${encodeURIComponent(funnelId)}?${params.toString()}`
  },
  MARKETING_FUNNEL_LEADS: (
    funnelId: string,
    step: string,
    fromDate: string,
    toDate: string,
    campaign?: string,
    limit = 50,
    offset = 0,
  ) => {
    const params = new URLSearchParams({
      step,
      from_date: fromDate,
      to_date: toDate,
      limit: String(limit),
      offset: String(offset),
    })
    if (campaign) params.set("campaign", campaign)
    return `/marketing/analytics/funnels/${encodeURIComponent(funnelId)}/leads?${params.toString()}`
  },
  MARKETING_EVENTS: (fromDate: string, toDate: string, campaign?: string) => {
    const params = new URLSearchParams({
      from_date: fromDate,
      to_date: toDate,
    })
    if (campaign) params.set("campaign", campaign)
    return `/marketing/analytics/events?${params.toString()}`
  },
  MARKETING_RECENT_EVENTS: (
    fromDate: string,
    toDate: string,
    options?: { campaign?: string; eventName?: string; limit?: number },
  ) => {
    const params = new URLSearchParams({
      from_date: fromDate,
      to_date: toDate,
    })
    if (options?.campaign) params.set("campaign", options.campaign)
    if (options?.eventName) params.set("event_name", options.eventName)
    if (options?.limit) params.set("limit", String(options.limit))
    return `/marketing/analytics/events/recent?${params.toString()}`
  },
  /** GET — finance: download B2B (enterprise) invoices as ZIP for a date range. Requires Finance access. */
  FINANCE_B2B_DOWNLOAD: (fromDate: string, toDate: string) =>
    `/finance/invoices/b2b/download?from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`,
  /** GET — finance: download B2C (individual) invoices as ZIP for a date range. Requires Finance access. */
  FINANCE_B2C_DOWNLOAD: (fromDate: string, toDate: string) =>
    `/finance/invoices/b2c/download?from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`,
  FINANCE_PAYMENTS_EXPORT: "/finance/payments/export",
  FINANCE_TRANSACTIONS: "/finance/transactions",
  FINANCE_TRANSACTION_DETAIL: (txnid: string) => `/finance/transactions/${txnid}`,
  FINANCE_INVOICES_LIST: (params: string) => `/finance/invoices?${params}`,
  FINANCE_INVOICE_PDF: (txnid: string) => `/finance/invoices/${encodeURIComponent(txnid)}/pdf`,
  FINANCE_INVOICES_DOWNLOAD: (params: string) => `/finance/invoices/download?${params}`,
  FINANCE_INVOICES_DOWNLOAD_SELECTED: (txnids: string[]) => {
    const p = new URLSearchParams()
    txnids.forEach(id => p.append('txnids', id))
    return `/finance/invoices/download-selected?${p.toString()}`
  },
  JD_VALIDATE: "/jd/validate",
  JD_EXTRACT: "/jd/extract",
  JD_SAVE: "/jd/save",
  /** POST — dev-only; seeds completed resume extraction without file upload (backend must enable ENABLE_RESUME_TEST_MODE). */
  RESUME_TEST_MODE: "/resumes/test-mode",
  /** GET — roles + experience levels for the role-based mock interview picker. */
  MOCK_INTERVIEW_ROLES: "/mock-interview/roles",
  /** GET — which entry options the candidate may see (resume / role-based). */
  MOCK_INTERVIEW_ENTRY_OPTIONS: "/mock-interview/entry-options",
  /** GET — role-relevant skills with parent-question counts per experience level. */
  MOCK_INTERVIEW_ROLE_SKILLS: (roleId: string, experienceLevelId?: string) =>
    `/mock-interview/roles/${encodeURIComponent(roleId)}/skills${
      experienceLevelId
        ? `?experience_level_id=${encodeURIComponent(experienceLevelId)}`
        : ""
    }`,
  /** POST — create a mock interview from role + up to five skills + experience. */
  MOCK_INTERVIEW_CREATE: "/mock-interview/interviews",
  /** POST with { phrase } for arbitrary TTS (e.g. system design probe questions). */
  JD_SPEECH_TTS: "/jd/speech-tts",
  REFERRAL_LINK: "/user/referral-link",
  SUBMIT_REFERRAL_CODE: "/user/submit-referral-code",
  REFERRAL_ATTEMPTS: "/user/referral-attempts",
  /** POST — record interest in a recommended course (lead capture). */
  RECOMMENDED_COURSE_INTEREST: "/user/recommended-course-interest",
  /** GET — whether the user already submitted interest for a course. */
  RECOMMENDED_COURSE_INTEREST_STATUS: (courseId: string) =>
    `/user/recommended-course-interest/status?course_id=${encodeURIComponent(courseId)}`,
  /** POST (session JWT) — claim next course eligibility row for logged-in user's email; credits course_attempts_balance. */
  COURSES_CLAIM: "/courses/claim",
  ACTIVE_COURSES: "/courses/active",
  /** GET — persisted course recommendation for an interview (assigns on first call). */
  INTERVIEW_COURSE_RECOMMENDATION: (interviewId: string) =>
    `/user/interviews/${encodeURIComponent(interviewId)}/course-recommendation`,
  ENTERPRISE_SEND_OTP_LOGIN: "/enterprise/send-otp-login",
  ENTERPRISE_SEND_OTP_REGISTER: "/enterprise/send-otp-register",
  ENTERPRISE_LOGIN: "/enterprise/login",
  ENTERPRISE_REGISTER: "/enterprise/register",
  ENTERPRISE_RECRUITER_AUTO_LOGIN: "/enterprise/recruiter/auto-login",
  /** Org owner onboarding and settings (Bearer, JWT role enterprise). */
  ENTERPRISE_SETUP_STATUS: "/enterprise/setup-status",
  ENTERPRISE_DISMISS_WORKFLOW_SETUP: "/enterprise/setup-status/dismiss-workflow",
  ENTERPRISE_COMPANY: "/enterprise/company",
  ENTERPRISE_TEMPLATES: "/enterprise/templates",
  ENTERPRISE_TEMPLATE_STAGE_TYPES: "/enterprise/templates/stage-types",
  ENTERPRISE_TEMPLATE_SUGGESTIONS: "/enterprise/templates/suggestions",
  ENTERPRISE_TEMPLATE_CLONE: "/enterprise/templates/clone",
  ENTERPRISE_TEMPLATE_DRAFT: "/enterprise/templates/draft",
  ENTERPRISE_TEMPLATE_BY_ID: (id: string) =>
    `/enterprise/templates/${encodeURIComponent(id)}`,
  ENTERPRISE_TEMPLATE_STAGES_BY_ID: (id: string) =>
    `/enterprise/templates/${encodeURIComponent(id)}/stages`,
  AUTH_SSO_LOGIN: "/auth/sso/login",
  AUTH_SSO_CALLBACK: "/auth/sso/callback",
  /** POST — create org owner from Google ID token + optional display name. */
  AUTH_SSO_REGISTER_OWNER: "/auth/sso/register-owner",
  AUTH_SEND_OTP: "/auth/send-otp",
  AUTH_VERIFY_OTP: "/auth/verify-otp",
  AUTH_ENTERPRISE_DETECT: "/auth/enterprise-detect",
  /** GET list (owner only) + POST invite — org owner only for both. */
  ENTERPRISE_RECRUITERS: "/enterprise/recruiters",
  /** POST — resend invite for an expired recruiter invite. */
  RESEND_RECRUITER_INVITE: (id: string) => `/enterprise/recruiters/${id}/resend-invite`,
  /** DELETE — soft-remove a recruiter (owner only). */
  REMOVE_RECRUITER: (id: string) => `/enterprise/recruiters/${id}`,
  /** POST — reactivate a previously removed recruiter (owner only). */
  REACTIVATE_RECRUITER: (id: string) => `/enterprise/recruiters/${id}/reactivate`,
  /** GET — pending JD ownership transfers awaiting owner action. */
  PENDING_JD_TRANSFERS: "/enterprise/jd-transfers/pending",
  /** POST — accept a pending JD ownership transfer (owner only). */
  ACCEPT_JD_TRANSFER: (id: string) => `/enterprise/jd-transfers/${id}/accept`,
  /** GET — lightweight counts for dashboards (owner only). */
  ENTERPRISE_RECRUITERS_METRICS: "/enterprise/recruiters/metrics",
  /** GET — weekly scheduled vs completed interview counts by day (owner only). */
  ENTERPRISE_WEEKLY_INTERVIEWS: "/enterprise/dashboard/weekly-interviews",
  /** GET — recent activity feed for the enterprise dashboard (owner only). */
  ENTERPRISE_RECENT_ACTIVITY: "/enterprise/dashboard/recent-activity",
  /** GET — aggregate candidate status counts for the candidates dashboard (owner or recruiter). */
  ENTERPRISE_CANDIDATES_STATS: "/enterprise/candidates/stats",
  /** GET — notifications for JWT user (owner or recruiter). Browser: proxied via `/api/enterprise/notifications`. */
  ENTERPRISE_NOTIFICATIONS: "/enterprise/notifications",
  /** PATCH JSON body `{ id }` — mark one notification read. Browser: `/api/enterprise/notifications/read`. */
  ENTERPRISE_NOTIFICATION_READ: "/enterprise/notifications/read",
  /** PATCH JSON body `{ ids: string[] }` — mark those read. Browser: `/api/enterprise/notifications/read-all`. */
  ENTERPRISE_NOTIFICATIONS_READ_ALL: "/enterprise/notifications/read-all",
  /** GET/PATCH — per-user notification prefs + enterprise no-show delay (owner). Browser: `/api/enterprise/notification-settings`. */
  ENTERPRISE_NOTIFICATION_SETTINGS: "/enterprise/notification-settings",
  /** POST JSON — bulk JD create (partial success; success + duplicates with temp_id). */
  ENTERPRISE_JD_BULK_UPLOAD: "/enterprise/jd/bulk-upload",
  /** Recruiter JD links (Bearer). GET list, POST create. */
  RECRUITER_JDS: "/recruiter/jds",
  /** GET — candidate pipeline counts for all JDs in one request. */
  RECRUITER_JDS_CANDIDATE_STATS: "/recruiter/jds/candidate-stats",
  /** GET — paginated, server-searched/filtered/sorted candidates across every accessible JD,
   * one call per page (replaces per-JD /jds/{jd_id}/candidates fan-out on the dashboard). */
  RECRUITER_CANDIDATES: (query: string) => `/recruiter/candidates?${query}`,
  /** GET — paginated job roles with stage metadata + candidate counts, one call per page (replaces per-JD /jds/{jd_id}/stages).
   * Optional status ("ACTIVE"|"INACTIVE"|"ALL"), search, sort query params. */
  RECRUITER_JDS_STAGES_PAGINATED: (
    page: number,
    limit: number,
    opts?: { status?: string; search?: string; sort?: string },
  ) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (opts?.status) params.set("status", opts.status);
    if (opts?.search && opts.search.trim()) params.set("search", opts.search.trim());
    if (opts?.sort) params.set("sort", opts.sort);
    return `/recruiter/jds/stages?${params.toString()}`;
  },
  /** PATCH JSON {title: string} — rename a job role's title. Only allowed while INACTIVE. */
  RECRUITER_JD_UPDATE_TITLE: (jdId: number) => `/recruiter/jds/${jdId}/title`,
  /** POST JSON — mark a job role ACTIVE. */
  RECRUITER_JD_ACTIVATE: (jdId: number) => `/recruiter/jds/${jdId}/activate`,
  /** POST JSON {cancel_interviews?: boolean} — mark a job role INACTIVE. */
  RECRUITER_JD_DEACTIVATE: (jdId: number) => `/recruiter/jds/${jdId}/deactivate`,
  /** POST JSON {jd_ids: number[]} — bulk activate. */
  RECRUITER_JDS_BULK_ACTIVATE: "/recruiter/jds/bulk-activate",
  /** POST JSON {jd_ids: number[], cancel_interviews?: boolean} — bulk deactivate. */
  RECRUITER_JDS_BULK_DEACTIVATE: "/recruiter/jds/bulk-deactivate",
  /** GET — poll progress of a background (cancel_interviews=true) deactivation. */
  RECRUITER_JD_DEACTIVATION_STATUS: (jdId: number) => `/recruiter/jds/${jdId}/deactivation-status`,
  /** GET — job role deactivation suggestions (stale-active / no-activity). */
  RECRUITER_JD_SUGGESTIONS: "/recruiter/jds/suggestions",
  /** GET — org-wide ACTIVE/INACTIVE job role counts, independent of the current tab. */
  RECRUITER_JD_STATUS_COUNTS: "/recruiter/jds/status-counts",
  /** POST JSON — create multiple JDs (partial success; returns success + failed with temp_id). */
  RECRUITER_JDS_BATCH: "/recruiter/jds/batch",
  /** POST multipart FormData field `file` — parse Title+Description from PDF/TXT (no Link: in file). */
  RECRUITER_JDS_PARSE_PDF: "/recruiter/jds/parse-pdf",
  /** POST multipart `file` — PDF/DOCX → preview behavioral question strings (no DB). */
  RECRUITER_JD_EXTRACT_BEHAVIORAL_QUESTIONS:
    "/recruiter/jds/stages/extract-behavioral-questions",
  /** GET — current recruiter profile (name, company) for dashboard welcome. */
  RECRUITER_ME: "/recruiter/me",
  /** POST JSON — create JD + pipeline stages (+ optional question sets) in one transaction. */
  RECRUITER_JDS_CREATE_WITH_STAGES: "/recruiter/jds/create-with-stages",
  /** POST multipart FormData field `file` — preview extraction (no DB assign). */
  RECRUITER_JD_CANDIDATES_EXTRACT: (jdId: number) =>
    `/recruiter/jds/${jdId}/candidates/extract`,
  /** POST multipart FormData — upload resume, returns SSE stream with extraction result. */
  RECRUITER_JD_CANDIDATES_EXTRACT_ASYNC: (jdId: number) =>
    `/recruiter/jds/${jdId}/candidates/async/extract`,
  /** POST JSON — assign candidate (user) to JD after confirmation. */
  RECRUITER_JD_CANDIDATES: (jdId: number) =>
    `/recruiter/jds/${jdId}/candidates`,
  /** GET — JD–candidate fitment (candidateId = user UUID). */
  RECRUITER_JD_CANDIDATE_FITMENT: (jdId: number, candidateId: string) =>
    `/recruiter/jds/${jdId}/candidates/${candidateId}/fitment`,
  /** GET — pipeline stages for a JD (ordered by stage_order). */
  RECRUITER_JD_STAGES: (jdId: number) => `/recruiter/jds/${jdId}/stages`,
  /** GET — stages + per-stage completed-candidate counts for a JD, in one call. */
  RECRUITER_JD_WORKFLOW_SUMMARY: (jdId: number) => `/recruiter/jds/${jdId}/workflow-summary`,
  /** GET — allowed stage types for recruiter JD stage configuration. */
  RECRUITER_JD_STAGE_TYPES: "/recruiter/jds/stage-types",
  /** POST JSON — one-time stage configuration for a JD (Bearer). */
  RECRUITER_JD_CONFIGURE_STAGES: (jdId: number) =>
    `/recruiter/jds/${jdId}/configure-stages`,
  /** PUT JSON — edit an already-configured JD's stages (Bearer). */
  RECRUITER_JD_UPDATE_STAGES: (jdId: number) =>
    `/recruiter/jds/${jdId}/stages`,
  /** GET — fetch saved behavioral questions for an assessment stage. */
  RECRUITER_JD_ASSESSMENT_BEHAVIORAL_GET: (jdId: number, stageId: string) =>
    `/recruiter/jds/${jdId}/stages/${stageId}/assessment-behavioral-questions`,
  /** PATCH JSON — edit assessment behavioral screening questions after stages are configured. */
  RECRUITER_JD_ASSESSMENT_BEHAVIORAL_PATCH: (jdId: number, stageId: string) =>
    `/recruiter/jds/${jdId}/stages/${stageId}/assessment-behavioral-questions`,
  /** POST — start candidate process / schedule first stage (recruiter Bearer). */
  CANDIDATE_START_PROCESS: "/candidate/start-process",
  /** GET — pipeline timeline + interview state (candidate_jd_attachments.id). */
  RECRUITER_CANDIDATE_PIPELINE: (candidateJdId: string) =>
    `/recruiter/candidate-jd/${candidateJdId}/pipeline`,
  /** POST — schedule next pipeline stage after assessment (recruiter Bearer). */
  RECRUITER_CANDIDATE_JD_SCHEDULE_NEXT: (candidateJdId: string) =>
    `/recruiter/candidate-jd/${candidateJdId}/schedule-next-stage`,
  /** POST — reschedule an abandoned/no-show stage (recruiter Bearer). */
  RECRUITER_CANDIDATE_JD_RESCHEDULE: (candidateJdId: string) =>
    `/recruiter/candidate-jd/${candidateJdId}/reschedule-stage`,
  /** GET — fresh presigned resume URL (recruiter Bearer). */
  RECRUITER_RESUME_URL: (candidateJdId: string) =>
    `/recruiter/candidate-jd/${candidateJdId}/resume-url`,
  /** GET — interview Q&A breakdown for recruiter (authorized via stage_interviews chain). */
  RECRUITER_INTERVIEW_SUMMARY: (interviewId: string) =>
    `/recruiter/interviews/${interviewId}/summary`,
  GET_SLOTS: "/interview/slots",
  SCHEDULE_STATUS: "/interview/schedule-status",
  SCHEDULE_INTERVIEW: "/interview/schedule",
  START_SCHEDULED_INTERVIEW: "/interview/start-scheduled",
  VALIDATE_INTERVIEW_TOKEN: (token: string) => `/interview/validate/${token}`,
  /** POST — HTTP Basic (user `schedule`, password = interview token) → JWT same shape as login. */
  SESSION_FROM_SCHEDULE_BASIC: "/interview/session-from-schedule-basic",
  /** POST — JSON `{ token }` → JWT (preferred; avoids Basic parsing issues in browsers). */
  SESSION_FROM_SCHEDULE_TOKEN: "/interview/session-from-schedule-token",
  // Courses API Endpoints
  COURSES: "/api/courses",
  COURSE_BY_ID: (id: string) => `/api/course/${id}`,
  TOPIC_INFO: (topicId: string, courseId: string) =>
    `/api/topic/${topicId}/info?courseId=${courseId}`,
  TOPIC_METADATA: (topicId: string, courseId: string) =>
    `/api/topic/${topicId}/metadata?courseId=${courseId}`,
  GET_SECTION: (topicId: string, sectionIndex: string) =>
    `/api/topic/${topicId}/section/${sectionIndex}`,
  ENROLLMENT: (enrollmentId: string) => `/api/enrollment/${enrollmentId}`,
  ENROLLMENT_BY_COURSE: (courseId: string) =>
    `/api/enrollment/course/${courseId}`,
  CREATE_ENROLLMENT: (courseId: string) => `/api/enrollment/course/${courseId}`,
  START_COURSE: (enrollmentId: string) =>
    `/api/enrollment/${enrollmentId}/start`,
  QUIZ: (quizId: string) => `/api/quiz/${quizId}`,
  QUIZ_METADATA: (quizId: string) => `/api/quiz/${quizId}/metadata`,
  START_QUIZ_ATTEMPT: (quizId: string) => `/api/quiz/${quizId}/start`,
  QUIZ_ATTEMPT_IN_PROGRESS: (quizId: string) =>
    `/api/quiz/${quizId}/attempt/in-progress`,
  QUIZ_QUESTION: (quizId: string, questionId: string) =>
    `/api/quiz/${quizId}/question/${questionId}`,
  SAVE_QUIZ_ANSWER: (quizId: string) => `/api/quiz/${quizId}/answer`,
  QUIZ_ATTEMPT: (quizId: string, attemptId: string) =>
    `/api/quiz/${quizId}/attempt/${attemptId}`,
  SUBMIT_QUIZ: (quizId: string) => `/api/quiz/${quizId}/submit`,
  EVALUATE_CODE: (topicId: string) => `/api/topic/${topicId}/evaluate-code`,
  SUBMIT_CODE: (topicId: string, sectionIndex: string) =>
    `/api/topic/${topicId}/section/${sectionIndex}/submit-code`,
  GET_PREVIOUS_SUBMISSION: (topicId: string, sectionIndex: string) =>
    `/api/topic/${topicId}/section/${sectionIndex}/submission`,
  GET_TEXT_HINT: (topicId: string, sectionIndex: string) =>
    `/api/topic/${topicId}/section/${sectionIndex}/hint`,
  GET_SOLUTION_CODE: (topicId: string, sectionIndex: string) =>
    `/api/topic/${topicId}/section/${sectionIndex}/hint/solution`,
  MARK_TOPIC_COMPLETE: (enrollmentId: string) =>
    `/api/enrollment/${enrollmentId}/topic/complete`,
  SKIP_TOPIC_V2: (enrollmentId: string, courseId: string, topicId: string) =>
    `/api/enrollment/${enrollmentId}/skip-topic?courseId=${encodeURIComponent(courseId)}&topicId=${encodeURIComponent(topicId)}`,
  UPDATE_ENROLLMENT_PROGRESS: (enrollmentId: string) =>
    `/api/enrollment/${enrollmentId}/progress`,
  START_COURSE_PAYMENT: "/api/payment/start",
  PAYMENT_STATUS_BY_COURSE: (courseId: string) =>
    `/api/payment/course/${courseId}/status`,
  ATTACHMENT_DOWNLOAD: (
    topicId: string,
    templateIndex: number,
    courseId: string,
  ) =>
    `/api/topic/${topicId}/attachment/${templateIndex}/download?courseId=${courseId}`,
  PROJECTS: "/api/projects",
  PROJECT_BY_ID: (id: string) => `/api/project/${id}`,
  PROJECT_ENROLLMENT: (projectId: string) =>
    `/api/enrollment/project/${projectId}`,
  CREATE_PROJECT_ENROLLMENT: (projectId: string) =>
    `/api/enrollment/project/${projectId}`,
  UPDATE_PROJECT_ENROLLMENT_PROGRESS: (projectId: string) =>
    `/api/enrollment/project/${projectId}/progress`,
  ADMIN_INTERVIEWS: "/admin/interviews",
  ADMIN_SCHEDULED_INTERVIEWS: "/admin/scheduled-interviews",
  ADMIN_PAYMENTS: "/admin/payments",
  ADMIN_PAYMENTS_EXPORT: "/admin/payments/export",
  ADMIN_TRANSACTIONS: "/admin/transactions",
  ADMIN_TRANSACTION_DETAIL: (txnid: string) => `/admin/transactions/${txnid}`,
  ADMIN_TOPICS: "/admin/topics",
  ADMIN_ROLES: "/admin/roles",
  ADMIN_INTERVIEW_INVITES: "/admin/interview-invites",
  ADMIN_INTERVIEW_INVITE_SUMMARY: (inviteId: string) =>
    `/admin/interview-invites/${inviteId}/summary`,
  ADMIN_INTERVIEW_INVITES_PARSE_JD: "/admin/interview-invites/parse-jd",
  ADMIN_INTERVIEW_INVITES_CHECK_JD: "/admin/interview-invites/check-jd",
  ADMIN_INTERVIEW_INVITE_CALENDAR: "/admin/interview-invites/calendar",
  ADMIN_INTERVIEW_INVITE_CALENDAR_AUTHORIZE:
    "/admin/interview-invites/calendar/authorize-url",
  ADMIN_EXPERIENCE_LEVELS: "/admin/experience-levels",
  /** GET: Neo4j AI-generated pool (excludes api_ingest); optional question_text filter on graph. */
  ADMIN_DAG_QUESTIONS: "/admin/dag-questions",
  /** GET: Neo4j manual-ingest questions; question_text filter hits graph only (USE_GRAPH_FOR_QUESTIONS). */
  ADMIN_MANUAL_QUESTIONS: "/admin/manual-questions",
  /** GET: Presigned S3 URL for graph question TTS (super-admin). */
  ADMIN_GRAPH_QUESTION_TTS_PRESIGNED: (
    graphQuestionId: string,
    ttsKind?: "elevenlabs" | "piper" | "unknown",
  ) =>
    `/admin/graph-questions/${encodeURIComponent(graphQuestionId)}/tts-presigned${
      ttsKind ? `?tts_kind=${encodeURIComponent(ttsKind)}` : ""
    }`,
  /** POST body: QuestionIngestRequest — see /questions/ingest in the federated API. */
  ADMIN_UPLOAD_MANUAL_QUESTIONS: "/questions/ingest",
  /** POST body: System Design v2 questions — see /questions/ingest-system-design. */
  ADMIN_INGEST_SYSTEM_DESIGN_QUESTIONS: "/questions/ingest-system-design",
  /** POST body: { topic_id, role_id, experience_level_id? } — availability-first manual trigger (Generate Questions page). */
  ADMIN_GENERATE_QUESTIONS_TRIGGER: "/questions/generate-dag/admin-trigger",
  /** POST body: { topic_id, role_id, experience_level_id? } — complete pending admin jobs so Generate is available again. */
  ADMIN_GENERATE_QUESTIONS_CANCEL: "/questions/generate-dag/admin/cancel",
  /** GET — SuperAdmin proxy of question-generator /health. */
  ADMIN_QUESTION_GENERATOR_HEALTH: "/questions/generator/health",
  ADMIN_QUESTION_BUNDLES: "/admin/question-bundles",
  ADMIN_PRACTICE_QUESTIONS: "/admin/practice-questions",
  ADMIN_PRACTICE_QUESTION: (id: string) => `/admin/practice-questions/${id}`,
  ADMIN_PRACTICE_QUESTION_TOGGLE: (id: string) =>
    `/admin/practice-questions/${id}/toggle-active`,
  ADMIN_SYSTEM_TEMPLATES: "/admin/system-templates",
  ADMIN_SYSTEM_TEMPLATE_BY_ID: (id: string) => `/admin/system-templates/${id}`,
  ADMIN_SYSTEM_TEMPLATE_STAGES: (id: string) => `/admin/system-templates/${id}/stages`,
  ADMIN_SYSTEM_TEMPLATE_STATUS: (id: string) => `/admin/system-templates/${id}/status`,
  // Support Bot Endpoints
  SUPPORT_THREADS_BASE: '/api/support/threads',
  SUPPORT_THREADS_LOOKUP: '/api/support/threads/lookup',
  SUPPORT_THREAD_BY_ID: (id: string) => `/api/support/threads/${encodeURIComponent(id)}`,
  SUPPORT_THREAD_SELECT: (id: string) =>
    `/api/support/threads/${encodeURIComponent(id)}/select`,
  SUPPORT_THREAD_CHAT: (id: string) =>
    `/api/support/threads/${encodeURIComponent(id)}/chat`,
  SUPPORT_THREAD_CLOSE: (id: string) =>
    `/api/support/threads/${encodeURIComponent(id)}/close`,
  SUPPORT_THREAD_RESTART: (id: string) =>
    `/api/support/threads/${encodeURIComponent(id)}/restart`,
  SUPPORT_THREAD_CLAIM: (id: string) =>
    `/api/support/threads/${encodeURIComponent(id)}/claim`,
  ADMIN_JOBS: "/admin/jobs",
  /** GET - admin: question generation history grouped by skill/topic + role. Pages GROUPS. */
  ADMIN_QUESTION_GENERATION_GROUPS: "/admin/question-generation/groups",
  /** GET ?role_id= — live Neo4j parent counts per skill × experience level. */
  ADMIN_QUESTION_GENERATION_COVERAGE: "/admin/question-generation/coverage",
  ADMIN_QUESTION_GENERATION_ROLE_SKILLS: "/admin/question-generation/role-skills",
  /** GET - admin: every bank counted once, per-role readiness, ranked gaps and insights. */
  ADMIN_QUESTION_GENERATION_SUMMARY: "/admin/question-generation/summary",
  ADMIN_JOB_MARK_COMPLETED: (jobId: string, jobType: string) =>
    `/admin/jobs/${jobId}/mark-completed?job_type=${jobType}`,
  ADMIN_JOB_RETRY: (jobId: string, jobType: string) =>
    `/admin/jobs/${jobId}/retry?job_type=${jobType}`,
  // Project content and editor (use enrollment id from GET/POST /api/enrollment/project/:projectId)
  PROJECT_CONTENT: (projectEnrollmentId: string) =>
    `/api/project-enrollment/${projectEnrollmentId}/content`,
  PROJECT_TASK_CONTENT: (projectEnrollmentId: string, taskId: string) =>
    `/api/project-enrollment/${projectEnrollmentId}/task/${taskId}`,
  PROJECT_EDITOR_STATE: (projectEnrollmentId: string) =>
    `/api/project-enrollment/${projectEnrollmentId}/editor/state`,
  PROJECT_EDITOR_NEXT: (projectEnrollmentId: string) =>
    `/api/project-enrollment/${projectEnrollmentId}/editor/next`,
  PROJECT_EDITOR_PREV: (projectEnrollmentId: string) =>
    `/api/project-enrollment/${projectEnrollmentId}/editor/prev`,
  PROJECT_EDITOR_SYNC: (projectEnrollmentId: string) =>
    `/api/project-enrollment/${projectEnrollmentId}/editor/sync`,
  PROJECT_EDITOR_NEXT_FILE: (projectEnrollmentId: string) =>
    `/api/project-enrollment/${projectEnrollmentId}/editor/next-file`,
  PROJECT_EDITOR_PREV_FILE: (projectEnrollmentId: string) =>
    `/api/project-enrollment/${projectEnrollmentId}/editor/prev-file`,
};

// On load in the browser, log any missing/empty NEXT_PUBLIC_* frontend env vars.
logMissingFrontendEnv();
